import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { sanitizeHtml } from '../../utils/sanitizers';

interface RichTextRendererProps {
  content: string;
  className?: string;
}

/**
 * Converts tiptap-extension-resize-image's proprietary HTML into standard,
 * sanitizer-friendly markup.
 *
 * The extension serialises images as:
 *   <figure>
 *     <img src="..." containerstyle="width:400px; margin:0 0 0 auto;" wrapperstyle="display:inline-block;float:right;..." />
 *   </figure>
 *
 * Neither `figure` nor the custom attributes survive DOMPurify, so all
 * alignment information is lost before rendering.  This function runs BEFORE
 * sanitisation and replaces each figure with a plain
 *   <div style="text-align: right|center|left"><img src="..." /></div>
 * which DOMPurify preserves and our CSS already styles correctly.
 */
const preprocessResizeImages = (html: string): string => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Target ALL imgs with the extension's custom attributes — they may be inside
    // <p>, <div>, <figure>, or anywhere else (0 figure elements were found in practice).
    doc.querySelectorAll<HTMLImageElement>('img[containerstyle], img[wrapperstyle]').forEach(img => {
      const containerStyle = (img.getAttribute('containerstyle') || '').toLowerCase();
      const wrapperStyle   = (img.getAttribute('wrapperstyle')   || '').toLowerCase();
      const combined = containerStyle + ' ' + wrapperStyle;

      // Derive the alignment-specific CSS from the float/margin values.
      // `containerstyle` reliably contains `float: right` / `float: left` for
      // right/left alignment, and `margin: 0 auto` for center.
      let alignStyle = '';
      if (combined.includes('float: right') || combined.includes('float:right')) {
        alignStyle = 'float: right; margin-left: 8px;';
      } else if (combined.includes('float: left') || combined.includes('float:left')) {
        alignStyle = 'float: left; margin-right: 8px;';
      } else if (
        combined.includes('margin: 0 auto') ||
        combined.includes('margin:0 auto') ||
        (combined.includes('margin-left: auto') && combined.includes('margin-right: auto'))
      ) {
        alignStyle = 'display: block; margin-left: auto; margin-right: auto;';
      }

      // Merge alignment into the img's existing style attribute.
      // DOMPurify allows the `style` attribute (it's in ALLOWED_ATTR) and
      // standard layout properties like `float` and `margin` pass its CSS filter.
      if (alignStyle) {
        const existing = (img.getAttribute('style') || '').replace(/;\s*$/, '');
        img.setAttribute('style', existing ? `${existing}; ${alignStyle}` : alignStyle);
      }

      // Remove the proprietary attributes so DOMPurify doesn't see unknown attrs.
      img.removeAttribute('containerstyle');
      img.removeAttribute('wrapperstyle');
    });

    // Also handle the figure-wrapper case (kept for future extension versions).
    doc.querySelectorAll('figure').forEach(figure => {
      const img = figure.querySelector('img');
      if (!img) return;
      const wrapper = doc.createElement('div');
      wrapper.style.margin = '0.5rem 0';
      wrapper.appendChild(img.cloneNode(true));
      figure.parentNode?.replaceChild(wrapper, figure);
    });

    return doc.body.innerHTML;
  } catch (e) {
    // Fall back to original string if anything goes wrong
    return html;
  }
};

const sanitizeMarkdownInput = (text: string) => {
  let sanitized = text;
  // Remove script tags and their content
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  
  // Remove other dangerous tags like iframe, object, embed, style, link, meta, base
  sanitized = sanitized.replace(/<(iframe|object|embed|link|meta|style|base)\b[^>]*>/gi, '');
  sanitized = sanitized.replace(/<\/(iframe|object|embed|link|meta|style|base)>/gi, '');

  // Remove inline event handlers like onerror, onload, onclick, etc.
  sanitized = sanitized.replace(/\son[a-z]+\s*=\s*(['"])(.*?)\1/gi, '');
  sanitized = sanitized.replace(/\son[a-z]+\s*=\s*([^>\s]+)/gi, '');
  
  // Remove javascript: pseudo-protocol
  sanitized = sanitized.replace(/javascript\s*:/gi, 'unsafe:');
  
  return sanitized;
};

/**
 * A component that safely renders either HTML or Markdown content.
 * It uses a simple heuristic to detect HTML, falling back to Markdown.
 */
const RichTextRenderer: React.FC<RichTextRendererProps> = ({ content, className = '' }) => {
  if (!content) return null;

  // Heuristic to detect legacy HTML vs Markdown
  const trimmedContent = content.trim();
  
  // 1. If it starts with an HTML tag, it's almost certainly legacy HTML
  const startsWithTag = /^<[a-z]/i.test(trimmedContent);
  
  // 2. If it contains common HTML block or formatting tags, it's likely HTML
  const hasHtmlBlocks = /<(p|div|ul|ol|li|table|h[1-6]|br|span|b|i|strong|em|u|s|sub|sup|img|a|mark)\b/i.test(content);
  
  // 3. If it has explicit Markdown indicators at the start of lines or for bold/italic
  const hasMarkdownIndicators = /^#|^\*|^>|^[0-9]+\.|^-\s|\*\*|~~|\[.*\]\(.*\)|`/m.test(content);
  
  // Determine if we should treat as HTML
  // We prioritize HTML if it starts with a tag or has significant HTML blocks and few/no markdown indicators
  const isHtml = startsWithTag || (hasHtmlBlocks && !hasMarkdownIndicators);

  const isSimple = !isHtml && !hasMarkdownIndicators;
  const isShort = trimmedContent.length < 100 && !trimmedContent.includes('\n');
  const combinedClassName = `rich-text-content ${(!isShort && !isSimple) ? 'prose prose-sm max-w-none' : ''} ${className}`;

  if (isSimple) {
    return (
      <span id="rich-text-simple" className={`rich-text-content ${className}`}>
        {content}
      </span>
    );
  }

  if (isHtml) {
    // Pre-process to convert tiptap-extension-resize-image's proprietary
    // figure/containerstyle/wrapperStyle structure into standard text-align divs
    // BEFORE DOMPurify runs (those custom attributes are not in the allowlist).
    const preprocessed = preprocessResizeImages(content);
    const sanitizedHtml = sanitizeHtml(preprocessed);
    return (
      <div 
        id="rich-text-html"
        className={combinedClassName}
        dangerouslySetInnerHTML={sanitizedHtml} 
      />
    );
  }

  // Otherwise, render as Markdown
  const safeContent = sanitizeMarkdownInput(content);

  const isSafeUrl = (url: string) => {
    try {
      const decoded = decodeURIComponent(url).trim().toLowerCase();
      // Block javascript, data, vbscript protocols
      if (decoded.startsWith("javascript:") || decoded.startsWith("data:") || decoded.startsWith("vbscript:")) {
        return false;
      }
      // Allow relative paths, HTTP, HTTPS, mailto, tel
      return decoded.startsWith("/") ||
             decoded.startsWith("./") ||
             decoded.startsWith("../") ||
             /^(https?|mailto|tel):/i.test(decoded);
    } catch (e) {
      return false;
    }
  };

  const sanitizeStyle = (styleObj: any) => {
    if (!styleObj || typeof styleObj !== 'object') return undefined;

    const allowedKeys = [
      'width', 'height', 'maxWidth', 'maxHeight', 'minWidth', 'minHeight',
      'margin', 'marginLeft', 'marginRight', 'marginTop', 'marginBottom',
      'padding', 'paddingLeft', 'paddingRight', 'paddingTop', 'paddingBottom',
      'float', 'display', 'textAlign', 'color', 'backgroundColor'
    ];

    const sanitized: any = {};
    for (const key of Object.keys(styleObj)) {
      if (allowedKeys.includes(key)) {
        const val = String(styleObj[key]).toLowerCase();
        // Block viewport relative units to prevent viewport hijacking overlays
        if (val.includes('vw') || val.includes('vh') || val.includes('vmin') || val.includes('vmax')) {
          continue;
        }
        sanitized[key] = styleObj[key];
      }
    }
    return sanitized;
  };

  const allowedElements = [
    'p', 'br', 'strong', 'em', 'strike', 'u', 'sub', 'sup',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'a', 'img', 'div', 'span', 'code', 'pre', 'hr',
    'blockquote', 'mark'
  ];

  return (
    <div id="rich-text-markdown" className={combinedClassName}>
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]} 
        rehypePlugins={[rehypeRaw]}
        allowedElements={allowedElements}
        components={{
          a: ({ node, href, children, style, ...props }) => {
            const safeHref = href && isSafeUrl(href) ? href : '#';
            const isExternal = safeHref.startsWith('http');
            const safeStyle = sanitizeStyle(style);
            return (
              <a
                href={safeHref}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                style={safeStyle}
                {...props}
              >
                {children}
              </a>
            );
          },
          img: ({ node, src, alt, style, containerstyle, wrapperstyle, ...props }: any) => {
            const safeSrc = src && isSafeUrl(src) ? src : '';
            const safeStyle = sanitizeStyle(style);
            return <img src={safeSrc} alt={alt} style={safeStyle} {...props} />;
          },
          div: ({ node, style, ...props }) => {
            const safeStyle = sanitizeStyle(style);
            return <div style={safeStyle} {...props} />;
          },
          span: ({ node, style, ...props }) => {
            const safeStyle = sanitizeStyle(style);
            return <span style={safeStyle} {...props} />;
          }
        }}
      >
        {safeContent}
      </ReactMarkdown>
    </div>
  );
};

export default RichTextRenderer;
